// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PlusCircle, Upload, BarChart2, Zap, X, Edit2, Sparkles } from 'lucide-react';

import {
  walletAPI,
  normaliseTotals,
} from '../../api/services/budget/walletService';
import { transactionAPI } from '../../api/services/budget/transactionService';
import categoryAPI from '../../api/services/budget/categoryService';
import testDataAPI from '../../api/services/testDataService';
import testTransactionAPI from '../../api/services/testTransactionService';
import { currentYear, currentMonth } from '../../utils/dates';
import { formatINR } from '../../utils/currency';

import TransactionList from '../../components/transactions/TransactionList';
import TransactionForm from '../../components/transactions/TransactionForm';
import Modal from '../../components/ui/Modal';
import { EditBalance } from '../../components/ui/EditBalance';
import CurrentMonthChart from '../../components/charts/budget/CurrentMonthChart';
import ExpenseTrendChart from '../../components/ExpenseTrendChart';
import ProfileChart from '../../components/charts/ProfileChart';
import SummaryHeader from './SummaryHeader';

export default function BudgetHome() {
  const [isInitialised, setIsInitialised] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isSetupComplete, setIsSetupComplete] = useState(
    localStorage.getItem('setupComplete') === 'true'
  );
  const [period, setPeriod] = useState('monthly');
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditBal, setShowEditBal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [testTxnLoading, setTestTxnLoading] = useState(false);
  const [testTxnMessage, setTestTxnMessage] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [walletRes, txRes] = await Promise.all([
        walletAPI.getTotals(),
        transactionAPI.list({
          year: currentYear(),
          month: currentMonth(),
        }),
      ]);
      // If server returns 404 or a specific 'NOT_INITIALIZED' flag
      if (
        walletRes.status === 204 ||
        walletRes.status === 404 ||
        !walletRes.data.is_setup
      ) {
        setIsInitialised(false);
        // Only show onboarding if setup is not complete
        if (!isSetupComplete) {
          setShowOnboarding(true);
        }
      }

      setWallet(normaliseTotals(walletRes.data));
      setTransactions(txRes.data?.results ?? txRes.data ?? []);
    } catch (error) {
      // keep empty defaults
      if (error.response?.status === 404 && !isSetupComplete) {
        setShowOnboarding(true);
      }
      console.log('Issue ar wallen getting' + error);
    } finally {
      setLoading(false);
    }
  }, [isSetupComplete]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePopulateTestTransactions = async () => {
    setTestTxnLoading(true);
    setTestTxnMessage('Generating ~100 test transactions...');

    try {
      const result = await testDataAPI.populateTestTransactions();

      if (result.success) {
        setTestTxnMessage(
          `✅ ${result.message}`
        );
        // Reload data to show new transactions
        setTimeout(() => {
          loadData();
        }, 1500);
      } else {
        setTestTxnMessage(`❌ ${result.message}`);
      }

      // Clear message after 6 seconds
      setTimeout(() => setTestTxnMessage(''), 6000);
    } catch (err) {
      setTestTxnMessage(`❌ Error: ${err.message || 'Failed to populate test transactions'}`);
      console.error(err);
    } finally {
      setTestTxnLoading(false);
    }
  };

  const filtered = transactions.filter((t) => {
    const q = searchTerm.toLowerCase();
    return (
      !q ||
      t.title?.toLowerCase().includes(q) ||
      t.type?.toLowerCase().includes(q) ||
      String(t.amount).includes(q)
    );
  });

  const balance = wallet?.net_balance ?? 0;

  if (loading) return <div className="p-8 text-center">Checking status...</div>;

  return (
    <div className="space-y-6">
      {/* ── Page Header ─────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {new Date().toLocaleDateString('en-IN', {
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <PlusCircle size={15} /> Add Transaction
          </button>
          <Link
            to="/apps/budgetfeed"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 bg-white text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Upload size={15} /> Import
          </Link>
          <Link
            to="/apps/budget/analytics"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 bg-white text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <BarChart2 size={15} /> Analysis
          </Link>
          <button
            onClick={handlePopulateTestTransactions}
            disabled={testTxnLoading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            title="[DEV] Generate ~100 random test transactions with categories & subcategories"
          >
            <Sparkles size={15} /> {testTxnLoading ? 'Generating...' : 'Test Transactions'}
          </button>
        </div>
      </div>
      {/* ── OnboardingModal───────────────────────────────────────── */}
      {showOnboarding && !isSetupComplete && (
        <OnboardingModal
          onComplete={() => {
            setIsSetupComplete(true);
            setShowOnboarding(false);
            loadData(); // Re-fetch data once DB is populated
          }}
        />
      )}

      {/* ── Test Transaction Message ─────────────────────────────── */}
      {testTxnMessage && (
        <div className={`p-3 rounded-lg border text-sm ${
          testTxnMessage.includes('✅')
            ? 'bg-green-50 border-green-200 text-green-700'
            : testTxnMessage.includes('⚠️')
            ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {testTxnMessage}
        </div>
      )}

      {/* ── Summary Strip ────────────────────────────────────────── */}
      <SummaryHeader />

      {/* ── Wallet Cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Net Balance',
            value: wallet?.net_balance,
            color: 'text-indigo-600',
          },
          {
            label: 'Total Income',
            value: wallet?.total_income,
            color: 'text-green-600',
          },
          {
            label: 'Total Expense',
            value: wallet?.total_expense,
            color: 'text-red-500',
          },
          {
            label: 'Savings',
            value: wallet?.total_savings,
            color: 'text-amber-500',
          },
        ].map(({ label, value, color }) => (
          <button
            key={label}
            onClick={
              label === 'Net Balance' ? () => setShowEditBal(true) : undefined
            }
            className={`bg-white rounded-xl border border-gray-200 p-4 text-left ${label === 'Net Balance' ? 'hover:border-indigo-300 cursor-pointer' : 'cursor-default'}`}
          >
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className={`text-xl font-semibold ${color}`}>
              {loading ? '—' : formatINR(value ?? 0)}
            </p>
          </button>
        ))}
      </div>

      {/* ── Period Toggle ─────────────────────────────────────────── */}
      <div className="flex gap-2">
        {['monthly', 'yearly'].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
              period === p
                ? 'bg-indigo-600 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* ── Charts ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Expense Trends
          </h3>
          <ExpenseTrendChart period={period} />
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Category Breakdown
          </h3>
          <ProfileChart data={transactions} />
        </div>
      </div>

      {/* ── Transactions ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-800">
            Recent Transactions
            <span className="ml-2 text-xs font-normal text-gray-400">
              {filtered.length} entries
            </span>
          </h2>
          <input
            type="text"
            placeholder="Search by remark or amount…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <TransactionList
          transactions={filtered}
          loading={loading}
          mobileLimit={10}
          tabletLimit={25}
          detailed={false}
        />

        <div className="p-4 border-t border-gray-100">
          <Link
            to="/apps/budget/transactions"
            className="block w-full text-center text-sm text-indigo-600 hover:underline"
          >
            View all transactions →
          </Link>
        </div>
      </div>

      {/* ── Modals ───────────────────────────────────────────────── */}
      {showAddForm && (
        <Modal
          isOpen={showAddForm}
          onClose={() => setShowAddForm(false)}
          title="Add Transaction"
        >
          <TransactionForm
            onClose={() => setShowAddForm(false)}
            onSuccess={() => {
              setShowAddForm(false);
              loadData();
            }}
          />
        </Modal>
      )}

      <EditBalance
        isOpen={showEditBal}
        handleState={setShowEditBal}
        currentBalance={balance}
        handleCurrentBalance={(v) =>
          setWallet((w) => ({ ...w, net_balance: v }))
        }
      />
    </div>
  );
}

const OnboardingModal = ({ onComplete }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [formData, setFormData] = useState({
    currency: 'INR',
    categories: [
      {
        name: 'Food',
        type: 'Expense',
        sub: [{ name: 'Groceries', isWant: false }],
      },
    ],
    accounts: [{ type: 'CASH' }],
  });
  const [categories, setCategories] = useState([
    { name: 'Income', type: 'INCOME', sub: [], budget: 0 },
    { name: 'Expenses', type: 'EXPENSE', sub: [], budget: 80 }, // Default 80%
    { name: 'Savings', type: 'SAVINGS', sub: [], budget: 20 }, // Default 20%
    { name: 'Transfer', type: 'TRANSFER', sub: [], budget: 0 },
  ]);

  const [newCat, setNewCat] = useState({ name: '', type: 'EXPENSE' });
  const [newSub, setNewSub] = useState({ parent: '', name: '', isWant: false });
  const [newCurr, setNewCurr] = useState(false);
  const [newCurrSymbol, setNewCurrSym] = useState('฿');
  const [newCurrency, setNewCurrency] = useState('');
  const [testDataLoading, setTestDataLoading] = useState(false);
  const [testDataMessage, setTestDataMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleAddCategory = () => {
    if (!newCat.name) return;
    // Add new category to state
    setCategories((prev) => [
      ...prev,
      {
        name: newCat.name,
        type: newCat.type,
        sub: [],
        budget: newCat.type === 'EXPENSE' ? 10 : 0, // Default 10% for expenses
      },
    ]);
    // Clear input
    setNewCat({ name: '', type: 'EXPENSE' });
  };

  const handleAddSubCategory = () => {
    if (!newSub.name || !newSub.parent) return;
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.name === newSub.parent) {
          return {
            ...cat,
            sub: [...cat.sub, { name: newSub.name, isWant: newSub.isWant }],
          };
        }
        return cat;
      }),
    );
    setNewSub({ ...newSub, name: '' }); // Clear input but keep parent
  };

  const handleDeleteSubCategory = (parentName, subName) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.name === parentName) {
          return {
            ...cat,
            sub: cat.sub.filter((s) => s.name !== subName),
          };
        }
        return cat;
      }),
    );
  };

  const handlePopulateTestData = async () => {
    setTestDataLoading(true);
    setTestDataMessage('Populating test categories...');
    try {
      const result = await testDataAPI.populateTestCategories();

      // Fetch all categories from backend to populate the structure
      const categoriesRes = await categoryAPI.list({ active: 'true' });
      const fetchedCategories = categoriesRes.data || [];

      // Transform backend categories to UI format
      const transformedCategories = fetchedCategories.reduce((acc, cat) => {
        const existing = acc.find((c) => c.name === cat.name);
        if (!existing) {
          acc.push({
            name: cat.name,
            type: cat.category_type,
            icon: cat.icon || '📁',
            color: cat.color,
            sub: cat.subcategories?.map((sub) => ({
              name: sub.name,
              icon: sub.icon,
              isWant: false,
            })) || [],
            budget: cat.category_type === 'EXPENSE' ? 80 : cat.category_type === 'SAVING' ? 20 : 0,
          });
        }
        return acc;
      }, []);

      // Update categories in state
      setCategories(transformedCategories);

      // Show success message
      if (result.created.length > 0) {
        setTestDataMessage(
          `✅ Created and loaded ${result.created.length} categories!`
        );
      } else if (result.skipped.length > 0) {
        setTestDataMessage(
          `✅ All ${result.skipped.length} test categories loaded and ready!`
        );
      } else {
        setTestDataMessage('✅ Test categories ready to use!');
      }

      setTimeout(() => setTestDataMessage(''), 4000);
    } catch (err) {
      setTestDataMessage('❌ Error: ' + (err.message || 'Failed to populate test data'));
      console.error(err);
    } finally {
      setTestDataLoading(false);
    }
  };

  const handleClearTestData = () => {
    // Clear all auto-generated categories (keep Income, Expenses, Savings, Transfer)
    setCategories([
      { name: 'Income', type: 'INCOME', sub: [], budget: 0 },
      { name: 'Expenses', type: 'EXPENSE', sub: [], budget: 80 },
      { name: 'Savings', type: 'SAVINGS', sub: [], budget: 20 },
      { name: 'Transfer', type: 'TRANSFER', sub: [], budget: 0 },
    ]);
    setTestDataMessage('✅ Cleared test data. Ready to populate again.');
    setTimeout(() => setTestDataMessage(''), 3000);
  };

  const handleSubmitSetup = async () => {
    setSubmitting(true);
    setSubmitMessage('Saving your categories...');

    try {
      // Get all categories from backend to check which ones exist
      const existingRes = await categoryAPI.list({ active: 'true' });
      const existingCategories = existingRes.data || [];
      const existingNames = new Set(existingCategories.map(c => c.name));

      // Create any new categories that don't exist yet
      const categoriesToCreate = categories.filter(cat => !existingNames.has(cat.name));

      for (const cat of categoriesToCreate) {
        try {
          await categoryAPI.create({
            name: cat.name,
            category_type: cat.type,
            icon: cat.icon || '📁',
            color: cat.color || '#9ca3af',
          });
        } catch (err) {
          console.error(`Failed to create category ${cat.name}:`, err);
        }
      }

      // Now add all subcategories to their parent categories
      for (const cat of categories) {
        // Find the parent category ID
        const parentCat = existingCategories.find(c => c.name === cat.name) ||
                         (categoriesToCreate.find(c => c.name === cat.name) && {name: cat.name});

        if (!parentCat && !categoriesToCreate.find(c => c.name === cat.name)) {
          continue; // Skip if parent not found
        }

        // Fetch the full category with ID
        const catRes = await categoryAPI.list({ active: 'true' });
        const fullParent = catRes.data.find(c => c.name === cat.name);

        if (fullParent) {
          // Add subcategories
          for (const sub of cat.sub || []) {
            try {
              await categoryAPI.addSubcategory(fullParent.id, {
                name: sub.name,
                icon: sub.icon || '📋',
              });
            } catch (err) {
              // Subcategory might already exist, that's ok
              console.log(`Subcategory ${sub.name} already exists or error:`, err.message);
            }
          }
        }
      }

      setSubmitMessage('✅ Categories saved successfully! Moving to dashboard...');

      // Mark setup as complete and transition to dashboard
      setTimeout(() => {
        // Scroll to top for smooth UX
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Mark setup as complete in localStorage
        localStorage.setItem('setupComplete', 'true');

        // Trigger fade-out animation
        setIsClosing(true);

        // Wait for fade-out animation to complete, then close modal
        setTimeout(() => {
          onComplete();
        }, 400); // Match the fade-out duration
      }, 1200);

    } catch (err) {
      setSubmitMessage(`❌ Error: ${err.message || 'Failed to save categories'}`);
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-md transition-all duration-400 ${
      isClosing ? 'opacity-0 pointer-events-none' : 'opacity-100'
    }`}>
      <div className={`bg-white w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row transition-all duration-400 transform ${
        isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
      }`}>
        {/* Left Side: Form Logic */}
        <div className="flex-1 p-8 overflow-y-auto border-r border-gray-100">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            <span className="text-indigo-600">Welcome</span>, Let's Setup your
            Budget
          </h2>

          {/* Section A: Defaults */}
          <div className="grid grid-cols-12 gap-4 mb-8">
            {/* Left Side: Onboarding Form */}
            <div className="flex flex-col col-span-7 gap-1 ">
              <div className="flex flex-wrap md:flex-nowrap flex-centre items-end items-center gap-3 w-full">
                <div className="flex">
                  <label className="block text-sm font-medium text-gray-700 min-w-[80px]">
                    Currency
                  </label>
                  {/* Container for the dropdown and optional inputs */}
                  {/* 1. Main Dropdown */}
                  <div className="flex-1">
                    <select
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      onChange={(e) => {
                        const isNew = e.target.value === 'ADD_NEW';
                        setNewCurr(isNew);
                      }}
                    >
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="ADD_NEW">Add New Currency...</option>
                    </select>
                  </div>
                </div>
                <div className="NewCurr">
                  {/* 2. Conditional Inputs (Rendered in the same row) */}
                  {newCurr && (
                    <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100 flex gap-2">
                      <div className="flex gap-2 animate-in items-center fade-in slide-in-from-left-2 duration-300">
                        <div className="w-16">
                          <label className="text-[10px] uppercase items-center font-bold text-indigo-400">
                            Symbol
                          </label>
                          <input
                            value={newCurrSymbol}
                            onChange={(e) => setNewCurrSym(e.target.value)}
                            className="w-full border-gray-300 rounded-md text-sm"
                            placeholder="฿"
                          />
                        </div>
                        <div className="w-32">
                          <label className="text-[10px] uppercase font-bold items-center text-indigo-400">
                            Currency Name
                          </label>
                          <input
                            value={newCurrency}
                            onChange={(e) => setNewCurrency(e.target.value)}
                            className="w-full border-gray-300 rounded-md text-sm"
                            placeholder="e.g. Bitcoin"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Container for the dropdown and optional inputs */}
              </div>

              {/* Section B: Categories & Sub-categories */}
              <div className="space-y-4">
                <h3 className="font-semibold text-indigo-600">
                  Categories & Budget
                </h3>
                {/* 1. Display Current Structure (The "Output" before the form) */}
                <div className="bg-gray-50 p-4 rounded-lg border border-dashed border-gray-300 mb-4">
                  <p className="text-xs font-bold text-gray-400 mb-2 uppercase">
                    Current Structure
                  </p>
                  <div className="font-mono text-sm text-gray-700">
                    {categories.map((cat) => (
                      <div key={cat.name}>
                        <div className="flex justify-between items-center">
                          <span>
                            {cat.name === 'Expenses' ? '📂' : '📁'} {cat.name}
                          </span>
                          <span className="text-gray-400">{cat.budget}%</span>
                        </div>
                        {cat.sub.map((s) => (
                          <div
                            key={s.name}
                            className="pl-6 text-gray-500 flex justify-between items-center px-2 rounded py-1 hover:bg-red-50 transition-colors"
                            onMouseEnter={(e) => {
                              const btn = e.currentTarget.querySelector('[data-delete-btn]');
                              if (btn) btn.style.display = 'flex';
                            }}
                            onMouseLeave={(e) => {
                              const btn = e.currentTarget.querySelector('[data-delete-btn]');
                              if (btn) btn.style.display = 'none';
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <span>└── {s.name}</span>
                              <span className="text-[10px] px-1 bg-gray-200 rounded">
                                {s.isWant ? 'Want' : 'Need'}
                              </span>
                            </div>
                            <button
                              data-delete-btn
                              onClick={() => handleDeleteSubCategory(cat.name, s.name)}
                              className="text-red-500 hover:text-red-700 transition-colors font-bold"
                              style={{ display: 'none' }}
                              title="Delete subcategory"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 2. Form to Add New Category */}
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-7">
                    <label className="text-xs font-medium text-gray-500">
                      New Category Name
                    </label>
                    <input
                      value={newCat.name}
                      onChange={(e) =>
                        setNewCat({ ...newCat, name: e.target.value })
                      }
                      className="w-full border-gray-300 rounded-md text-sm"
                      placeholder="e.g. Healthcare"
                    />
                  </div>
                  <div className="col-span-5 flex gap-2">
                    <button
                      onClick={handleAddCategory}
                      className="bg-indigo-50 text-indigo-600 px-3 py-2 rounded-md text-sm font-medium hover:bg-indigo-100 w-full"
                    >
                      + Add
                    </button>
                  </div>
                </div>

                {/* 3. Conditional Sub-category Form (Visible if a category is selected) */}
                <div className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-100">
                  <p className="text-xs font-bold text-indigo-400 uppercase mb-3">
                    Add Sub-category
                  </p>
                  <div className="space-y-3">
                    <select
                      value={newSub.parent}
                      onChange={(e) =>
                        setNewSub({ ...newSub, parent: e.target.value })
                      }
                      className="w-full border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Select Parent Category...</option>
                      {categories.map((c) => (
                        <option key={c.name} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>

                    {newSub.parent && (
                      <div className="flex gap-2 items-center">
                        <input
                          value={newSub.name}
                          onChange={(e) =>
                            setNewSub({ ...newSub, name: e.target.value })
                          }
                          className="flex-1 border-gray-300 rounded-md text-sm"
                          placeholder="Sub-category name..."
                        />
                        <label className="flex items-center gap-1 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={newSub.isWant}
                            onChange={(e) =>
                              setNewSub({ ...newSub, isWant: e.target.checked })
                            }
                          />
                          Want?
                        </label>
                        <button
                          onClick={handleAddSubCategory}
                          className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm"
                        >
                          Add
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* DEV: Test Data Buttons */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 font-medium mb-2 uppercase">
                    Development Tools
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handlePopulateTestData}
                      disabled={testDataLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed font-medium transition-colors"
                      title="[DEV] Auto-fill with sample categories and subcategories"
                    >
                      <Zap size={18} />
                      {testDataLoading ? 'Loading...' : 'Populate'}
                    </button>
                    <button
                      onClick={handleClearTestData}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-200 text-red-700 rounded-lg hover:bg-red-300 font-medium transition-colors"
                      title="[DEV] Clear test data and reset to defaults"
                    >
                      <X size={18} />
                      Clear
                    </button>
                  </div>
                  {testDataMessage && (
                    <p className="text-center text-sm mt-2 px-2 py-1.5 bg-yellow-50 rounded border border-yellow-100 text-gray-700">
                      {testDataMessage}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
                  <button
                    onClick={handleSubmitSetup}
                    disabled={submitting || categories.length === 0}
                    className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed font-semibold transition-all flex items-center justify-center gap-2 text-base"
                    title="Save all categories and proceed to your dashboard"
                  >
                    {submitting ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Saving & Loading Dashboard...
                      </>
                    ) : (
                      <>
                        ✓ Complete Setup & Go to Dashboard
                      </>
                    )}
                  </button>
                  {submitMessage && (
                    <p className={`text-center text-sm px-2 py-1.5 rounded border ${
                      submitMessage.includes('✅')
                        ? 'bg-green-50 border-green-100 text-gray-700'
                        : submitMessage.includes('❌')
                        ? 'bg-red-50 border-red-100 text-gray-700'
                        : 'bg-blue-50 border-blue-100 text-gray-700'
                    }`}>
                      {submitMessage}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side: Tree Preview (Hidden on small mobile) */}
            <div className="hidden col-span-5 lg:block w-80 bg-gray-50 p-6 overflow-y-auto">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
                Structure Preview
              </h3>
              <pre className="text-sm font-mono text-gray-600">
                {`Budget
├── Expense
│   ├── Food
│   │   └── Groceries (Need)
│   └── Transport
│       └── Petrol (Need)
├── Income
│   ├── Salary
│   └── Misc
└── Savings`}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
